class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink) return;

        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
            if (event.code.toUpperCase() === 'SPACE') {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
            this.classList.add('animate', 'active');
        });

        this.addEventListener(
            'transitionend',
            () => {
                const containerToTrapFocusOn = this.classList.contains('is-empty')
                    ? this.querySelector('.drawer__inner-empty')
                    : document.getElementById('CartDrawer');
                const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true }
        );

        document.body.classList.add('overflow-hidden');
    }

    close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute('role', 'button');
        cartDrawerNote.setAttribute('aria-expanded', 'false');

        if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
            cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
        }

        cartDrawerNote.addEventListener('click', (event) => {
            event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
        });

        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState) {
        this.querySelector('.drawer__inner').classList.contains('is-empty') &&
            this.querySelector('.drawer__inner').classList.remove('is-empty');
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);

            if (!sectionElement) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        setTimeout(() => {
            this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
            this.open();
        });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
        return [
            {
                id: 'cart-drawer',
                selector: '#CartDrawer',
            },
            {
                id: 'cart-icon-bubble',
            },
        ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
    getSectionsToRender() {
        return [
            {
                id: 'CartDrawer',
                section: 'cart-drawer',
                selector: '.drawer__inner',
            },
            {
                id: 'cart-icon-bubble',
                section: 'cart-icon-bubble',
                selector: '.shopify-section',
            },
        ];
    }
}

customElements.define('cart-drawer-items', CartDrawerItems);


class CartUpsell extends HTMLElement {
    constructor() {
        super();
        this.config = {
            upsellCount: 8,
            recommendationStrategy: 'related',
            sectionTitle: 'You Might Also Like'
        };
        this.state = {
            isLoading: true,
            currentProducts: [],
            newProducts: []
        };
    }

    connectedCallback() {
        this.initializeConfiguration();
        this.render();
        this.initUpsells();
    }

    initializeConfiguration() {
        this.config.upsellCount = parseInt(this.getAttribute('data-product-count') || this.config.upsellCount);
        this.config.sectionTitle = this.getAttribute('data-section-title') || this.config.sectionTitle;
        this.config.recommendationStrategy = this.getAttribute('data-recommendation-strategy') || this.config.recommendationStrategy;
    }

    render() {
        this.innerHTML = `
      <div class="cart-upsell-container">
        <h2 class="cart-upsell-title">${this.config.sectionTitle}</h2>
        ${this.renderLoadingOverlay()}
        <div class="swiper cart-upsell-slider" aria-busy="true">
          <div class="swiper-wrapper">
         
          </div>
        </div>
      </div>
    `;
    }

    renderLoadingOverlay() {
        return `
      <div class="upsell-loading-overlay">
        <div class="upsell-loading-spinner"></div>
      </div>
    `;
    }

    async initUpsells() {
        try {
            const recommendedProducts = await this.fetchRecommendedProducts();

            // Hide the entire section if no products are available
            if (!recommendedProducts || recommendedProducts.length === 0) {
                this.style.display = 'none';
                return;
            }

            this.state.newProducts = recommendedProducts;
            this.state.isLoading = false;
            this.updateUpsellProducts();
        } catch (error) {
            console.error('Upsell initialization failed:', error);
            this.style.display = 'none';
        }
    }

    async fetchRecommendedProducts() {
        const cartItems = await this.getCartItems();

        // Check if cart is empty, return empty array instead of fetching fallback products
        if (!cartItems || cartItems.length === 0) {
            return [];
        }

        const productId = cartItems[0].product_id;

        try {
            const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${this.config.upsellCount}&intent=${this.config.recommendationStrategy}`);

            if (!response.ok) {
                throw new Error('Failed to fetch recommended products');
            }

            const { products } = await response.json();
            return products;
        } catch (error) {
            console.error('Error fetching recommendations:', error);
            return []; // Return empty array on error
        }
    }

    updateUpsellProducts() {
        const wrapper = this.querySelector('.swiper-wrapper');
        wrapper.innerHTML = ''; // Clear loading overlay

        // Add existing products first if any
        if (this.state.currentProducts.length) {
            this.state.currentProducts.forEach(product => {
                wrapper.appendChild(this.createProductSlide(product));
            });
        }

        // Add new products
        this.state.newProducts.forEach(product => {
            if (product.tags.includes('Gift')) return; // Exclude gifts
            wrapper.appendChild(this.createProductSlide(product));
        });

        this.querySelector('.upsell-loading-overlay').classList.add('hidden')

        // Update current products
        this.state.currentProducts = [...this.state.currentProducts, ...this.state.newProducts];
        this.state.newProducts = [];

        this.addCartEventListeners();
        this.reinitializeSwiper();
    }

    createProductSlide(product) {
        const slide = document.createElement('div');
        slide.classList.add('swiper-slide', 'upsell-product');
        slide.dataset.productId = product.id;
        
        // Check if sale prices are enabled and product is not a subscription
        const salePricesEnabled = window.salePricesConfig && window.salePricesConfig.enabled;
        const shouldApplySale = salePricesEnabled;
        
        // Calculate prices
        let originalPrice = product.price;
        let compareAtPrice = product.compare_at_price || product.price;
        let displayPrice = originalPrice;
        let priceHTML = '';
        
        if (shouldApplySale) {
            // Use compare_at_price if available, otherwise use regular price as base
            const basePrice = compareAtPrice > originalPrice ? compareAtPrice : originalPrice;
            const discountPercent = window.salePricesConfig.discountPercent || 40;
            const discountMultiplier = 1 - (discountPercent / 100);
            displayPrice = Math.round(basePrice * discountMultiplier);
            
            // Show compare at price (strikethrough) and sale price
            priceHTML = `
            <div class="upsell-product__price-wrapper">
                <span class="visually-hidden">Regular price</span>
                    <s class="upsell-product__old-price">${Shopify.formatMoney(basePrice)}</s>
                    <span class="visually-hidden">Sale price</span>
                    <span class="upsell-product__price">${Shopify.formatMoney(displayPrice)}</span>
                </div>
            `;
        } else {
            // Show regular price only
            priceHTML = `<span class="upsell-product__price">${Shopify.formatMoney(displayPrice)}</span>`;
        }
        
        slide.innerHTML = `
       <div class="upsell-product__media">
        <img src="${product.featured_image}" alt="${product.title}" width="150" height="150" loading="lazy">
      </div>
      <a href="${product.url}" class="upsell-product__title">${product.title}</a>
      <div class="upsell-product__row">
        ${priceHTML}
        <product-form>
          <form
                  method="post"
                  action="/cart/add"
                  id="product-form-template--19051402297590__main"
                  accept-charset="UTF-8"
                  class="form"
                  enctype="multipart/form-data"
                  ovalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="id" value="${product.variants[0].id}" class="product-variant-id">
            <button
                    class="add-to-cart-btn button--link button upsell-product__atc"
                    aria-haspopup="dialog"
                    type="submit"
                    data-variant-id="${product.variants[0].id}"
            >
              <span>Add</span>
              <div class="loading__spinner hidden">
  <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
  </svg>
</div>
            </button>
          </form>
        </product-form>
      </div>
    `;
        return slide;
    }

    reinitializeSwiper() {
        // Destroy existing Swiper instance if it exists
        if (this.swiper) {
            this.swiper.destroy();
        }

        // Reinitialize Swiper
        this.swiper = new Swiper(this.querySelector('.swiper'), {
            slidesPerView: 'auto',
            spaceBetween: 15,
            grabCursor: true,
            mousewheel: {
                invert: false,
                // Sideways swipes only, so a vertical scroll over the upsells still scrolls the cart.
                forceToAxis: true,
            },
            // cssMode: true
        });
    }

    addCartEventListeners() {
        const buttons = this.querySelectorAll('.add-to-cart-btn');
        buttons.forEach(button => {
            button.addEventListener('click', this.addToCart.bind(this));
        });
    }

    async addToCart(event) {
        const variantId = event.target.dataset.variantId;

        try {
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    id: variantId,
                    quantity: 1
                })
            });

            document.dispatchEvent(new CustomEvent('cart:updated'));
        } catch (error) {
            console.error('Add to cart failed:', error);
        }
    }

    async getCartItems() {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        return cart.items;
    }
}

customElements.define('cart-upsell', CartUpsell);